import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import { FaUsers, FaCoins, FaBan, FaFileDownload, FaHistory, FaHdd, FaUserPlus, FaTimes, FaPlay, FaClipboardList, FaCalculator, FaInfoCircle, FaFile, FaLayerGroup } from 'react-icons/fa';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

// Helper function to parse credit breakdown from transaction description
const parseCreditBreakdown = (description, amount) => {
  if (!description) return null;

  // Pattern: "Play: play_id on X files (Y pages) - 2 base + Z page credits"
  const playMatch = description.match(/Play:\s*(\S+)(?:\s*\(assembly\))?\s+on\s+(\d+)\s+files?\s+\((\d+)\s+pages?\)\s*-\s*(\d+)\s+base\s*\+\s*(\d+)\s+page\s+credits?/i);

  if (playMatch) {
    return {
      type: 'play',
      playId: playMatch[1],
      isAssembly: description.includes('(assembly)'),
      fileCount: parseInt(playMatch[2]),
      pageCount: parseInt(playMatch[3]),
      baseCredits: parseInt(playMatch[4]),
      pageCredits: parseInt(playMatch[5]),
      totalCredits: Math.abs(amount)
    };
  }

  // Pattern for uploads: "Upload: filename (X pages)"
  const uploadMatch = description.match(/Upload:\s*(.+?)\s+\((\d+)\s+pages?\)/i);
  if (uploadMatch) {
    return {
      type: 'upload',
      filename: uploadMatch[1],
      pageCount: parseInt(uploadMatch[2]),
      totalCredits: 0
    };
  }

  // Pattern for adjustments
  if (description.toLowerCase().includes('adjustment')) {
    return {
      type: 'adjustment',
      description: description,
      totalCredits: Math.abs(amount)
    };
  }

  return null;
};

const UserAdminDashboard = ({ readOnly = false }) => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [transactionView, setTransactionView] = useState('all');
  const [loginHistory, setLoginHistory] = useState([]);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [newUser, setNewUser] = useState({ fullName: '', email: '', password: '', password2: '', role: 'User' });
  const [creatingUser, setCreatingUser] = useState(false);
  const [plays, setPlays] = useState([]);
  const [togglingPlay, setTogglingPlay] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  const shownTransactionCount = transactions.length;
  const consumingTransactionCount = transactions.filter((t) => t.amount < 0).length;
  const nonConsumingTransactionCount = shownTransactionCount - consumingTransactionCount;
  
  const headers = {
    Authorization: `Bearer ${sessionStorage.getItem("userToken")}`,
    Accept: 'application/json'
  };

  useEffect(() => {
    fetchData();
    fetchPlays();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionView]);

  const fetchData = async () => {
    try {
      const statsRes = await axios.get(`${process.env.REACT_APP_BE_MAIN_URL}/user/admin/org/stats/`, { headers });
      setStats(statsRes.data);
      
      const usersRes = await axios.get(`${process.env.REACT_APP_BE_MAIN_URL}/user/admin/org/users/`, { headers });
      setUsers(usersRes.data);

      const includeAll = transactionView === 'all' ? 'true' : 'false';
      const txRes = await axios.get(`${process.env.REACT_APP_BE_MAIN_URL}/user/admin/org/transactions/?limit=20&include_all=${includeAll}`, { headers });
      setTransactions(txRes.data);

      const loginRes = await axios.get(`${process.env.REACT_APP_BE_MAIN_URL}/user/admin/org/login-history/?days=30`, { headers });
      setLoginHistory(loginRes.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchPlays = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BE_MAIN_URL}/user/admin/org/plays/`, { headers });
      setPlays(res.data.plays || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleTogglePlay = async (playId, currentEnabled) => {
    if (readOnly) return;
    setTogglingPlay(playId);
    try {
      await axios.post(
        `${process.env.REACT_APP_BE_MAIN_URL}/user/admin/org/plays/`,
        { play_id: playId, action: currentEnabled ? 'disable' : 'enable' },
        { headers }
      );
      toast.success(`Play ${currentEnabled ? 'disabled' : 'enabled'} successfully`);
      fetchPlays();
    } catch (err) {
      toast.error('Failed to update play');
    } finally { 
      setTogglingPlay(null);
    }
  };

  // Real-time validation function
  const validateField = (fieldName, value, allValues = newUser) => {
    const errors = {};
    
    switch(fieldName) {
      case 'fullName':
        if (!value || value.trim().length === 0) {
          errors.fullName = 'Full name is required';
        } else if (!/^[a-zA-Z0-9_ ]+$/.test(value)) {
          errors.fullName = 'Only letters, numbers, spaces, and underscores allowed';
        } else if (value.trim().length < 2) {
          errors.fullName = 'Name must be at least 2 characters';
        }
        break;
        
      case 'email':
        if (!value || value.trim().length === 0) {
          errors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          errors.email = 'Please enter a valid email address';
        }
        break;
        
      case 'password':
        if (!value || value.length === 0) {
          errors.password = 'Password is required';
        } else if (value.length < 8) {
          errors.password = 'Password must be at least 8 characters';
        } else if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) {
          errors.password = 'Password must contain at least one special character';
        }
        // Also validate password2 match if it's been touched
        if (allValues.password2 && value !== allValues.password2) {
          errors.password2 = 'Passwords do not match';
        }
        break;
        
      case 'password2':
        if (!value || value.length === 0) {
          errors.password2 = 'Please confirm your password';
        } else if (value !== allValues.password) {
          errors.password2 = 'Passwords do not match';
        }
        break;
      default:
        break;
    }
    
    return errors;
  };

  // Handle field changes with validation
  const handleFieldChange = (fieldName, value) => {
    setNewUser(prev => ({...prev, [fieldName]: value}));
    
    // Validate only if field has been touched
    if (touchedFields[fieldName]) {
      const errors = validateField(fieldName, value, {...newUser, [fieldName]: value});
      setValidationErrors(prev => {
        const updated = {...prev};
        if (Object.keys(errors).length > 0) {
          Object.assign(updated, errors);
        } else {
          delete updated[fieldName];
        }
        return updated;
      });
    }
  };

  // Handle field blur (mark as touched)
  const handleFieldBlur = (fieldName) => {
    setTouchedFields(prev => ({...prev, [fieldName]: true}));
    const errors = validateField(fieldName, newUser[fieldName]);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(prev => ({...prev, ...errors}));
    }
  };

  // Validate all fields
  const validateAllFields = () => {
    const allErrors = {};
    ['fullName', 'email', 'password', 'password2'].forEach(field => {
      const errors = validateField(field, newUser[field]);
      Object.assign(allErrors, errors);
    });
    return allErrors;
  };

  // Check if form is valid
  const isFormValid = () => {
    const allErrors = validateAllFields();
    return Object.keys(allErrors).length === 0 && 
           newUser.fullName && newUser.email && newUser.password && newUser.password2;
  };

  // const handleCreateUser = async (e) => {
  //   e.preventDefault();
    
  //   // Mark all fields as touched
  //   setTouchedFields({
  //     fullName: true,
  //     email: true,
  //     password: true,
  //     password2: true
  //   });
    
  //   // Validate all fields
  //   const allErrors = validateAllFields();
  //   if (Object.keys(allErrors).length > 0) {
  //     setValidationErrors(allErrors);
  //     toast.error('Please fix all validation errors before submitting');
  //     return;
  //   }
    
  //   setCreatingUser(true);
  //   try {
  //     // await axios.post(`${process.env.REACT_APP_BE_MAIN_URL}/user/admin/org/users/`, newUser, { headers });
  //     // toast.success("User created successfully!");
  //     const response = await axios.post(`${process.env.REACT_APP_BE_MAIN_URL}/user/admin/org/users/`, newUser, { headers });
  //     const successMsg = response.data?.msg || response.data?.message || "User created successfully!";
  //     toast.success(successMsg);
  //     setShowCreateUserModal(false);
  //     setNewUser({ fullName: '', email: '', password: '', password2: '', role: 'User' });
  //     setValidationErrors({});
  //     setTouchedFields({});
  //     fetchData();
  //   } catch (err) {
  //     console.error(err);
  //     const msg = err.response?.data?.msg || err.response?.data?.detail || "Failed to create user";
  //     // Handle Django Rest Framework validation errors dictionary
  //     if (typeof err.response?.data === 'object' && !msg) {
  //        Object.values(err.response.data).forEach(errorArray => {
  //            if (Array.isArray(errorArray)) errorArray.forEach(e => toast.error(e));
  //        });
  //     } else {
  //        toast.error(msg);
  //     }
  //   } finally {
  //     setCreatingUser(false);
  //   }
  // };


    const handleCreateUser = async (e) => {
  e.preventDefault();

  // Mark all fields as touched
  setTouchedFields({
    fullName: true,
    email: true,
    password: true,
    password2: true,
  });

  // Validate all fields
  const allErrors = validateAllFields();

  if (Object.keys(allErrors).length > 0) {
    setValidationErrors(allErrors);
    toast.error("Please fix all validation errors before submitting");
    return;
  }

  setCreatingUser(true);

  try {
    const response = await axios.post(
      `${process.env.REACT_APP_BE_MAIN_URL}/user/admin/org/users/`,
      newUser,
      { headers }
    );

    const successMsg =
      response.data?.msg ||
      response.data?.message ||
      "User created successfully!";

    toast.success(successMsg, {
      position: "top-right",
      autoClose: 3000,
    });

    setShowCreateUserModal(false);

    setNewUser({
      fullName: "",
      email: "",
      password: "",
      password2: "",
      role: "User",
    });

    setValidationErrors({});
    setTouchedFields({});

    fetchData();
  } catch (err) {
    console.error("Create User Error:", err);

    const errorData = err.response?.data;

    // Handle validation errors like:
    // {
    //   email: ["user with this Email already exists."],
    //   fullName: ["user with this fullName already exists."]
    // }
    if (errorData && typeof errorData === "object") {
      let hasFieldErrors = false;

      Object.entries(errorData).forEach(([field, errors]) => {
        if (Array.isArray(errors)) {
          hasFieldErrors = true;

          errors.forEach((error) => {
            toast.error(error, {
              position: "top-right",
              autoClose: 3000,
            });
          });
        }
      });

      if (hasFieldErrors) {
        return;
      }
    }

    // Generic fallback message
    const msg =
      errorData?.msg ||
      errorData?.message ||
      errorData?.detail ||
      "Failed to create user";

    toast.error(msg, {
      position: "top-right",
      autoClose: 3000,
    });
  } finally {
    setCreatingUser(false);
  }
};
  const handleKillSession = async (userId) => {
    if (readOnly) return;
    try {
      await axios.post(`${process.env.REACT_APP_BE_MAIN_URL}/user/admin/org/users/${userId}/kill/`, {}, { headers });
      toast.success("User session terminated");
      fetchData();
    } catch (err) {
      toast.error("Failed to kill session");
    }
  };

  const handleExport = async () => {
      try {
           const includeAll = transactionView === 'all' ? 'true' : 'false';
           const response = await axios.get(`${process.env.REACT_APP_BE_MAIN_URL}/user/admin/org/export/transactions/?include_all=${includeAll}`, {
            headers,
            responseType: 'blob', // Important for file download
          });
          
          // Create a link to download the file
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          const exportType = transactionView === 'all' ? 'all_activity' : 'credit_usage';
          link.setAttribute('download', `Credit_History_${exportType}_${new Date().toISOString().slice(0,10)}.xlsx`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
      } catch (err) {
          console.error(err);
          toast.error("Failed to export transactions");
      }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">
          User Admin Dashboard {readOnly && <span className="text-sm bg-gray-200 text-gray-700 px-2 py-1 rounded ml-2">Read Only</span>}
        </h1>
        <div className="flex gap-2">
            {!readOnly && (
                <button 
                    onClick={() => setShowCreateUserModal(true)}
                    className="flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-700 transition shadow-sm"
                >
                    <FaUserPlus /> Create User
                </button>
            )}
            <button 
                onClick={handleExport}
                className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition shadow-sm"
            >
                <FaFileDownload /> Export Credit History
            </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-4 rounded shadow flex items-center border border-gray-100">
             <div className="p-3 bg-green-100 text-green-500 rounded-full mr-4">
               <FaCoins size={24} />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Credit Balance</p>
              <p className="text-2xl font-bold text-green-600">{stats.credit_balance}</p>
            </div>
          </div>
          <div className="bg-white p-4 rounded shadow flex items-center border border-gray-100">
             <div className="p-3 bg-indigo-100 text-indigo-500 rounded-full mr-4">
               <FaHdd size={24} />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Storage Used</p>
               <p className="text-2xl font-bold text-indigo-900">{stats.storage_used_gb || 0} <span className="text-sm font-normal text-gray-400">GB</span></p>
            </div>
          </div>
          <div className="bg-white p-4 rounded shadow flex items-center border border-gray-100">
             <div className="p-3 bg-blue-100 text-blue-500 rounded-full mr-4">
               <FaUsers size={24} />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Active Sessions</p>
              <p className="text-2xl font-bold text-gray-800">{stats.active_sessions || 0}</p>
            </div>
          </div>
           <div className="bg-white p-4 rounded shadow flex items-center border border-gray-100">
             <div className="p-3 bg-orange-100 text-orange-500 rounded-full mr-4">
               <FaHistory size={24} />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Total Users</p>
               <p className="text-2xl font-bold text-gray-800">{stats.total_users}</p>
            </div>
          </div>
        </div>
      )}

      {/* Concurrent Sessions Chart */}
      {loginHistory.length > 0 && (
        <div className="bg-white rounded shadow border border-gray-100 mb-6">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-700">Peak Concurrent Sessions (Last 30 Days)</h2>
            <p className="text-xs text-gray-500">Maximum simultaneous users per day vs. pooled license limit</p>
          </div>
          <div className="p-4" style={{ height: 250 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={loginHistory} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getMonth() + 1}/${date.getDate()}`;
                  }}
                />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} domain={[0, (dataMax) => Math.max(dataMax, stats?.max_concurrent_logins || 5) + 1]} />
                <Tooltip
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                  formatter={(value, name) => [value, name === 'concurrent' ? 'Peak Concurrent' : name]}
                />
                <Line
                  type="monotone"
                  dataKey="concurrent"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ fill: '#3b82f6', strokeWidth: 2, r: 3 }}
                  activeDot={{ r: 5 }}
                  connectNulls={true}
                  isAnimationActive={false}
                />
                <ReferenceLine
                  y={stats?.max_concurrent_logins || 5}
                  stroke="#ef4444"
                  strokeDasharray="5 5"
                  strokeWidth={2}
                  label={{
                    value: `License Limit: ${stats?.max_concurrent_logins || 5}`,
                    position: 'insideTopRight',
                    fill: '#ef4444',
                    fontSize: 11,
                    fontWeight: 'bold'
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Users Table */}
        <div className="bg-white rounded shadow overflow-hidden border border-gray-100">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-700">Organization Users</h2>
            </div>
            <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-500">
                <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                <tr>
                    <th className="px-6 py-3">User</th>
                    <th className="px-6 py-3">Role</th>
                    <th className="px-6 py-3">Status</th>
                    <th className="px-6 py-3">Action</th>
                </tr>
                </thead>
                <tbody>
                {users.map((user) => (
                    <tr key={user.id} className="bg-white border-b hover:bg-gray-50">
                    <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{user.fullName}</div>
                        <div className="text-xs text-gray-500">{user.email}</div>
                    </td>
                    <td className="px-6 py-4">
                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                            {user.role === 'User' ? 'Standard User' : user.role === 'Useradmin' ? 'Org Admin' : user.role}
                        </span>
                    </td>
                    <td className="px-6 py-4">
                        {user.is_online ? (
                            <span className="text-green-500 flex items-center gap-1 font-medium"><span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span> Online</span>
                        ) : (
                            <span className="text-gray-400">Offline</span>
                        )}
                    </td>
                    <td className="px-6 py-4">
                        {user.is_online && !readOnly && (
                            <button 
                                onClick={() => handleKillSession(user.id)}
                                className="text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 px-2 py-1 rounded transition text-xs font-bold uppercase tracking-wider"
                                title="Kill Session"
                            >
                                <FaBan className="inline mr-1" /> Kill
                            </button>
                        )}
                    </td>
                    </tr>
                ))}
                </tbody>
            </table>
            </div>
        </div>

        {/* Recent Transactions */}
        <div className="bg-white rounded shadow overflow-hidden border border-gray-100">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-700">Recent Credit Usage</h2>
                  <div className="flex items-center gap-3">
                    <div className="inline-flex rounded-md border border-gray-200 bg-white p-0.5 text-xs">
                      <button
                        onClick={() => setTransactionView('all')}
                        className={`px-2.5 py-1 rounded ${transactionView === 'all' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                      >
                        All Activity
                      </button>
                      <button
                        onClick={() => setTransactionView('usage')}
                        className={`px-2.5 py-1 rounded ${transactionView === 'usage' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                      >
                        Usage Only
                      </button>
                    </div>
                    <div className="group relative">
                      <FaInfoCircle className="text-gray-400 hover:text-blue-500 cursor-help" />
                      <div className="absolute right-0 top-6 w-72 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-xl">
                        <div className="font-bold mb-2 text-blue-300">How Credits Work</div>
                        <div className="space-y-2">
                          <div className="bg-gray-800 rounded p-2">
                            <div className="text-green-300 font-semibold">Base cost: 2 credits per play</div>
                          </div>
                          <div className="bg-gray-800 rounded p-2">
                            <div className="text-orange-300 font-semibold mb-1">+ Per PDF cost:</div>
                            <div className="text-gray-300 text-[11px] space-y-0.5 pl-2">
                              <div>1-3 pages = <span className="text-white font-medium">1 credit</span></div>
                              <div>4-6 pages = <span className="text-white font-medium">2 credits</span></div>
                              <div>7-9 pages = <span className="text-white font-medium">3 credits</span></div>
                              <div className="text-gray-400 italic">...and so on</div>
                            </div>
                          </div>
                          <div className="border-t border-gray-600 my-2"></div>
                          <div className="text-gray-300">
                            <span className="font-semibold text-blue-300">Example:</span> 3 PDFs
                          </div>
                          <div className="bg-gray-800 rounded p-2 text-[11px]">
                            <div className="flex justify-between"><span>PDF 1 (1 page)</span><span className="text-white">1 credit</span></div>
                            <div className="flex justify-between"><span>PDF 2 (4 pages)</span><span className="text-white">2 credits</span></div>
                            <div className="flex justify-between"><span>PDF 3 (7 pages)</span><span className="text-white">3 credits</span></div>
                            <div className="border-t border-gray-600 my-1"></div>
                            <div className="flex justify-between font-semibold">
                              <span>Total</span>
                              <span className="text-yellow-300">2 base + 6 = 8 credits</span>
                            </div>
                          </div>
                          <div className="text-gray-400 italic text-[10px]">Uploads are always free</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {transactionView === 'all'
                    ? 'Showing all activity (play usage + free uploads + adjustments)'
                    : 'Showing credit-consuming activity only'}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                  <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-700 border border-gray-200">
                    {shownTransactionCount} shown
                  </span>
                  <span className="px-2 py-1 rounded-full bg-red-50 text-red-700 border border-red-200">
                    {consumingTransactionCount} consuming
                  </span>
                  <span className="px-2 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                    {nonConsumingTransactionCount} non-consuming
                  </span>
                </div>
            </div>
            <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-500">
                <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                <tr>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Activity</th>
                    <th className="px-4 py-3">Cost Breakdown</th>
                    <th className="px-4 py-3 text-right">Total</th>
                </tr>
                </thead>
                <tbody>
                {transactions.map((t) => {
                    const breakdown = parseCreditBreakdown(t.description, t.amount);
                    return (
                    <tr key={t.id} className="bg-white border-b hover:bg-gray-50">
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                        {new Date(t.created_at).toLocaleDateString()}
                        <div className="text-gray-400">{new Date(t.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</div>
                    </td>
                    <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {t.action_type === 'PLAY' && <FaPlay className="text-purple-500" size={12} />}
                          {t.action_type === 'UPLOAD' && <FaFile className="text-blue-500" size={12} />}
                          {t.action_type === 'ADJUSTMENT' && <FaCoins className="text-yellow-500" size={12} />}
                          <span className="font-medium text-gray-900">
                            {breakdown?.type === 'play'
                              ? (breakdown.playId === 'tariff_v1' 
                                  ? 'Tariff Analysis' 
                                  : breakdown.playId === 'cost_reduction_v1' 
                                  ? 'Cost Reduction'
                                  : breakdown.playId === 'veeco_design_review_v1' 
                                  ? 'Design Review' 
                                  : breakdown.playId === 'veeco_drawing_checker_v1'
                                  ? 'Drawing Checker'
                                  : breakdown.playId)
                              : t.action_type === 'UPLOAD' ? 'File Upload' : t.action_type}
                          </span>
                          {breakdown?.isAssembly && (
                            <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">Assembly</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5">by {t.user_email}</div>
                    </td>
                    <td className="px-4 py-3">
                        {breakdown?.type === 'play' ? (
                          <div className="space-y-1.5">
                            <div className="flex items-center gap-3 text-xs">
                              <span className="flex items-center gap-1 text-gray-600">
                                <FaFile className="text-gray-400" size={10} />
                                {breakdown.fileCount} PDF{breakdown.fileCount !== 1 ? 's' : ''}
                              </span>
                              <span className="flex items-center gap-1 text-gray-600">
                                <FaLayerGroup className="text-gray-400" size={10} />
                                {breakdown.pageCount} page{breakdown.pageCount !== 1 ? 's' : ''} total
                              </span>
                            </div>
                            <div className="flex items-center gap-1 text-xs">
                              <span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded font-medium">
                                {breakdown.baseCredits} base
                              </span>
                              <span className="text-gray-400">+</span>
                              <span
                                className="bg-orange-50 text-orange-700 px-1.5 py-0.5 rounded font-medium cursor-help"
                                title={`1 credit per 3 pages for each of the ${breakdown.fileCount} PDF${breakdown.fileCount !== 1 ? 's' : ''}`}
                              >
                                {breakdown.pageCredits} PDF credits
                              </span>
                            </div>
                            <div className="text-[10px] text-gray-400">
                              1 credit per 3 pages, calculated per PDF
                            </div>
                          </div>
                        ) : breakdown?.type === 'upload' ? (
                          <div className="text-xs text-gray-500">
                            <span className="bg-green-50 text-green-700 px-1.5 py-0.5 rounded">FREE</span>
                            <span className="ml-2">{breakdown.pageCount} pages uploaded</span>
                          </div>
                        ) : breakdown?.type === 'adjustment' ? (
                          <div className="text-xs text-gray-500 italic">Admin adjustment</div>
                        ) : (
                          <div className="text-xs text-gray-500 truncate max-w-[180px]" title={t.description}>
                            {t.description}
                          </div>
                        )}
                    </td>
                    <td className="px-4 py-3 text-right">
                        <span className={`text-lg font-bold ${t.amount < 0 ? 'text-red-500' : t.amount > 0 ? 'text-green-500' : 'text-gray-400'}`}>
                            {t.amount > 0 ? '+' : ''}{t.amount}
                        </span>
                        <div className="text-xs text-gray-400">credits</div>
                    </td>
                    </tr>
                  );
                })}
                 {transactions.length === 0 && (
                  <tr>
                      <td colSpan="4" className="px-6 py-4 text-center text-gray-400 italic">No recent transactions</td>
                  </tr>
              )}
                </tbody>
            </table>
            </div>
        </div>
      </div>

      {/* Available Plays Management */}
      {plays.length > 0 && (
        <div className="bg-white rounded shadow border border-gray-100 mt-6">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
              <FaPlay className="text-purple-500" /> Available Plays
            </h2>
            <p className="text-xs text-gray-500">Enable or disable analysis plays for your organization</p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {plays.map((play) => (
                <div
                  key={play.id}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    play.enabled
                      ? 'border-purple-200 bg-purple-50'
                      : 'border-gray-200 bg-gray-50 opacity-60'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-full ${play.enabled ? 'bg-purple-100 text-purple-600' : 'bg-gray-200 text-gray-400'}`}>
                        {play.icon === 'clipboard-list' ? <FaClipboardList size={20} /> : <FaCalculator size={20} />}
                      </div>
                      <div>
                        <h3 className={`font-semibold ${play.enabled ? 'text-gray-800' : 'text-gray-500'}`}>
                          {play.name}
                        </h3>
                        <p className="text-xs text-gray-500 mt-1">{play.description}</p>
                      </div>
                    </div>
                    {!readOnly && (
                      <button
                        onClick={() => handleTogglePlay(play.id, play.enabled)}
                        disabled={togglingPlay === play.id}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          play.enabled ? 'bg-purple-500' : 'bg-gray-300'
                        } ${togglingPlay === play.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            play.enabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    )}
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      play.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'
                    }`}>
                      {play.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

       {/* Create User Modal */}
       {showCreateUserModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                  <div className="px-6 py-4 bg-gradient-to-r from-teal-600 to-teal-700 border-b border-teal-800 flex justify-between items-center">
                      <div>
                        <h3 className="text-lg font-bold text-white">Create New User</h3>
                        <p className="text-teal-100 text-xs mt-0.5">Add a new user to your organization</p>
                      </div>
                      <button 
                        onClick={() => {
                          setShowCreateUserModal(false);
                          setNewUser({ fullName: '', email: '', password: '', password2: '', role: 'User' });
                          setValidationErrors({});
                          setTouchedFields({});
                        }} 
                        className="text-white hover:bg-teal-800 p-1.5 rounded transition-colors"
                      >
                        <FaTimes />
                      </button>
                  </div>
                  <form onSubmit={handleCreateUser} className="p-6 space-y-5">
                      {/* Full Name Field */}
                      <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Full Name <span className="text-red-500">*</span>
                          </label>
                          <input 
                              type="text"
                              value={newUser.fullName} 
                              onChange={e => handleFieldChange('fullName', e.target.value)}
                              onBlur={() => handleFieldBlur('fullName')}
                              className={`w-full px-4 py-2.5 border-2 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none transition-colors ${
                                validationErrors.fullName && touchedFields.fullName
                                  ? 'border-red-300 bg-red-50'
                                  : touchedFields.fullName && !validationErrors.fullName && newUser.fullName
                                  ? 'border-green-300 bg-green-50'
                                  : 'border-gray-200'
                              }`}
                              placeholder="Enter full name (e.g., John Doe)"
                          />
                          <div className="mt-1.5 min-h-[20px]">
                            {validationErrors.fullName && touchedFields.fullName ? (
                              <p className="text-xs text-red-600 flex items-center gap-1">
                                <span>⚠</span> {validationErrors.fullName}
                              </p>
                            ) : (
                              <p className="text-xs text-gray-500">
                                Letters, numbers, spaces, and underscores only
                              </p>
                            )}
                          </div>
                      </div>

                      {/* Email Field */}
                      <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Email Address <span className="text-red-500">*</span>
                          </label>
                          <input 
                              type="email"
                              value={newUser.email} 
                              onChange={e => handleFieldChange('email', e.target.value)}
                              onBlur={() => handleFieldBlur('email')}
                              className={`w-full px-4 py-2.5 border-2 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none transition-colors ${
                                validationErrors.email && touchedFields.email
                                  ? 'border-red-300 bg-red-50'
                                  : touchedFields.email && !validationErrors.email && newUser.email
                                  ? 'border-green-300 bg-green-50'
                                  : 'border-gray-200'
                              }`}
                              placeholder="user@example.com"
                          />
                          <div className="mt-1.5 min-h-[20px]">
                            {validationErrors.email && touchedFields.email ? (
                              <p className="text-xs text-red-600 flex items-center gap-1">
                                <span>⚠</span> {validationErrors.email}
                              </p>
                            ) : (
                              <p className="text-xs text-gray-500">
                                User will login with this email address
                              </p>
                            )}
                          </div>
                      </div>

                      {/* Password Fields */}
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Password <span className="text-red-500">*</span>
                            </label>
                            <input 
                                type="password"
                                value={newUser.password} 
                                onChange={e => handleFieldChange('password', e.target.value)}
                                onBlur={() => handleFieldBlur('password')}
                                className={`w-full px-4 py-2.5 border-2 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none transition-colors ${
                                  validationErrors.password && touchedFields.password
                                    ? 'border-red-300 bg-red-50'
                                    : touchedFields.password && !validationErrors.password && newUser.password
                                    ? 'border-green-300 bg-green-50'
                                    : 'border-gray-200'
                                }`}
                                placeholder="Enter secure password"
                            />
                            <div className="mt-1.5 min-h-[20px]">
                              {validationErrors.password && touchedFields.password ? (
                                <p className="text-xs text-red-600 flex items-center gap-1">
                                  <span>⚠</span> {validationErrors.password}
                                </p>
                              ) : (
                                <div className="text-xs text-gray-500 space-y-0.5">
                                  <div className="flex items-center gap-1.5">
                                    <span className={newUser.password.length >= 8 ? 'text-green-600' : 'text-gray-400'}>✓</span>
                                    At least 8 characters
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <span className={/[!@#$%^&*(),.?":{}|<>]/.test(newUser.password) ? 'text-green-600' : 'text-gray-400'}>✓</span>
                                    Contains special character (!@#$%^&*...)
                                  </div>
                                </div>
                              )}
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Confirm Password <span className="text-red-500">*</span>
                            </label>
                            <input 
                                type="password"
                                value={newUser.password2} 
                                onChange={e => handleFieldChange('password2', e.target.value)}
                                onBlur={() => handleFieldBlur('password2')}
                                className={`w-full px-4 py-2.5 border-2 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none transition-colors ${
                                  validationErrors.password2 && touchedFields.password2
                                    ? 'border-red-300 bg-red-50'
                                    : touchedFields.password2 && !validationErrors.password2 && newUser.password2
                                    ? 'border-green-300 bg-green-50'
                                    : 'border-gray-200'
                                }`}
                                placeholder="Re-enter password"
                            />
                            <div className="mt-1.5 min-h-[20px]">
                              {validationErrors.password2 && touchedFields.password2 ? (
                                <p className="text-xs text-red-600 flex items-center gap-1">
                                  <span>⚠</span> {validationErrors.password2}
                                </p>
                              ) : newUser.password2 && newUser.password === newUser.password2 ? (
                                <p className="text-xs text-green-600 flex items-center gap-1">
                                  <span>✓</span> Passwords match
                                </p>
                              ) : (
                                <p className="text-xs text-gray-500">
                                  Must match the password above
                                </p>
                              )}
                            </div>
                        </div>
                      </div>

                      {/* Role Selection */}
                      <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            User Role <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={newUser.role}
                            onChange={e => setNewUser({...newUser, role: e.target.value})}
                            className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white"
                          >
                              <option value="User">Customer - Standard User</option>
                              <option value="Useradmin">Admin - Organization Administrator</option>
                              {(sessionStorage.getItem('userRole') === 'SystemAdmin' || sessionStorage.getItem('userRole') === 'System Admin') && (
                                <option value="SystemAdmin">System Administrator</option>
                              )}
                          </select>
                          <p className="text-xs text-gray-500 mt-1.5">
                              {newUser.role === 'SystemAdmin'
                                ? '⚙️ Full system-wide access across all organizations'
                                : newUser.role === 'Useradmin'
                                ? '🔑 Full access to organization settings and user management'
                                : '👤 Can view and edit projects within the organization'}
                          </p>
                      </div>

                      {/* Action Buttons */}
                      <div className="pt-2 flex justify-end gap-3 border-t border-gray-100 mt-6">
                          <button 
                            type="button" 
                            onClick={() => {
                              setShowCreateUserModal(false);
                              setNewUser({ fullName: '', email: '', password: '', password2: '', role: 'User' });
                              setValidationErrors({});
                              setTouchedFields({});
                            }}
                            className="px-5 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                          >
                            Cancel
                          </button>
                          <button 
                            type="submit" 
                            disabled={creatingUser || !isFormValid()}
                            className={`px-6 py-2.5 rounded-lg font-medium transition-all flex items-center gap-2 ${
                              creatingUser || !isFormValid()
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-teal-600 text-white hover:bg-teal-700 shadow-sm hover:shadow'
                            }`}
                            title={!isFormValid() ? 'Please fill all required fields correctly' : ''}
                          >
                            {creatingUser ? (
                              <>
                                <span className="animate-spin">⏳</span>
                                Creating...
                              </>
                            ) : (
                              <>
                                <FaUserPlus />
                                Create User
                              </>
                            )}
                          </button>
                      </div>
                  </form>
              </div>
          </div>
      )}
      <ToastContainer />
    </div>
  );
};

export default UserAdminDashboard;